import { Bar } from "react-chartjs-2"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons"
import { Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js"
import {
    DeliveryType,
    TimePeriodMeetingService,
    getDeliveryTypeValue,
} from "../../production/meetingService/meetingServiceTypes"
import { DashboardDataCard, DashboardDataContents } from "../../../../dashboards/components/DashboardDataCard"
import { MerchantHomeQuery, fetchMeetingServiceLimit, fetchMeetingServiceOrdersPerHour } from "../merchantHomeApi"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { Box } from "@mui/system"
import { faUserGroup } from "@fortawesome/pro-light-svg-icons"
import { OrganizerType } from "../../../../../shared/types/OrganizerTypes"
import { OrderPackageSlip } from "../../production/meetingService/components/OrderPackageSlip"
import { useState } from "react"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend)

const useStyles = makeStyles()((theme) => ({
    container: {
        gap: "1.75rem",
    },
    card: {
        width: "100%",
        height: "auto",
    },
    graphWrapper: {
        width: "100%",
        height: "100%",
    },

    tableHeadTextColor: {
        color: theme.palette.tailwind.slate[500],
    },
    tableRow: {
        "& > td": {
            color: theme.palette.tailwind.slate[900],
        },
    },
    titleButton: {
        cursor: "pointer",
        color: "#2196F3",
        marginRight: "-0.75rem",
    },
    accentColor: {
        color: theme.palette.tailwind.slate[500],
    },
    badge: {
        "& .MuiBadge-badge": {
            background: theme.palette.common.black,
            color: theme.palette.common.white,
            right: "-0.5rem",
            minWidth: "1rem",
            minHeight: "1rem",
        },
    },
    badgeIcon: {
        color: "#7bf1a8",
        fontSize: "1rem",
    },
    secondaryLine: {
        color: theme.palette.tailwind.slate[500],
    },
    cellMainText: {
        color: theme.palette.tailwind.slate[700],
    },
    cellTime: {
        color: theme.palette.tailwind.slate[900],
        fontSize: "0.875rem",
    },
}))

export function MeetingServiceInfoDeliveryPlanning() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()

    const { data } = useApiQuery(fetchMeetingServiceOrdersPerHour, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDERS_PER_HOURS,
        dependencies: {
            merchantId,
            period: TimePeriodMeetingService.TODAY.toUpperCase(),
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const labels = data
        ? data.meetingServiceSale.salesPerHour.map((item) => {
              const fromTime = item.period.from.time.substring(0, 2)
              const toTime = item.period.to.time.substring(0, 2)
              return `${fromTime} - ${toTime}`
          })
        : []

    const chartData = {
        labels,
        datasets: [
            {
                label: l10n.getString("merchant-home-meeting-service-delivery-planning-delivery"),
                data: data
                    ? data.meetingServiceSale.salesPerHour.map(
                          (item) =>
                              item.deliveryTypes?.find((type) => type.name === DeliveryType.DELIVERY)?.salesSummary
                                  ?.numberOfOrders || 0,
                      )
                    : 0,
                backgroundColor: "#ffb1c1",
                borderColor: "#ff6484",
                borderWidth: 2,
                borderRadius: 0,
                borderSkipped: false,
            },
            {
                label: l10n.getString("merchant-home-meeting-service-delivery-planning-picked-up"),
                data: data
                    ? data.meetingServiceSale.salesPerHour.map(
                          (item) =>
                              item.deliveryTypes?.find((type) => type.name === DeliveryType.DELIVERY)?.salesSummary
                                  ?.numberOfOrders || 0,
                      )
                    : 0,
                backgroundColor: "#9bd0f5",
                borderColor: "#36a2eb",
                borderWidth: 2,
                borderRadius: 0,
                borderSkipped: false,
            },
            {
                label: l10n.getString("merchant-home-meeting-service-delivery-planning-eating-in-canteen"),
                data: data
                    ? data.meetingServiceSale.salesPerHour.map(
                          (item) =>
                              item.deliveryTypes?.find((type) => type.name === DeliveryType.EATING_IN_CANTEEN)
                                  ?.salesSummary?.numberOfOrders || 0,
                      )
                    : 0,
                backgroundColor: "#a4dfdf",
                borderColor: "#4bc0c0",
                borderWidth: 2,
                borderRadius: 0,
                borderSkipped: false,
            },
        ],
    }

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "bottom" as const,
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                stepSize: 10,
            },
        },
    }

    return (
        <Grid container>
            <Bar data={chartData} options={options} />
        </Grid>
    )
}

const NUMBER_OF_ORDERS = 5

export function MeetingServiceInfoNextDeliveries() {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const [showPackSlipModal, setShowPackSlipModal] = useState(false)
    const [selectedOrderId, setSelectedOrderId] = useState<number | undefined>()

    const { data } = useApiQuery(fetchMeetingServiceLimit, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_LIMIT,
        dependencies: {
            merchantId,
            limit: NUMBER_OF_ORDERS,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const handleRowClick = (orderId: number) => {
        setSelectedOrderId(orderId)
        setShowPackSlipModal(true)
    }

    const handleClosePackSlip = () => {
        setShowPackSlipModal(false)
        setSelectedOrderId(undefined)
    }

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.tableHeadTextColor}>
                            <LocalizedStrict id="merchant-home-meeting-service-table-delivery-column">
                                <>Delivery</>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell className={classes.tableHeadTextColor}>
                            <LocalizedStrict id="merchant-home-meeting-service-table-name-column">
                                <>Name</>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data &&
                        data.orders.map((order) => {
                            const deliveryType = getDeliveryTypeValue(DeliveryType.PICKED_UP)
                            const creatorPerson = order.organizers?.find((org) => org.type === OrganizerType.CREATOR)

                            return (
                                <TableRow
                                    key={order.id}
                                    className={classes.tableRow}
                                    onClick={() => handleRowClick(order.id)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <TableCell>
                                        <Stack flexDirection="row" gap={3}>
                                            <Stack
                                                alignItems="center"
                                                flexDirection="row"
                                                gap={1}
                                                className={classes.cellTime}
                                            >
                                                {dateTimeFactory
                                                    .fromApi(order.deliveryDetails.time)
                                                    .toFormat("d/M HH:mm")}
                                            </Stack>
                                            <Stack gap="0.5rem">
                                                <Typography variant="body2" className={classes.cellMainText}>
                                                    {deliveryType}
                                                </Typography>
                                                {deliveryType !== DeliveryType.EATING_IN_CANTEEN && (
                                                    <Typography variant="caption" className={classes.secondaryLine}>
                                                        {order.deliveryDetails.location.name}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack gap="0.5rem">
                                            <Stack direction="row" gap="0.5rem">
                                                <Typography variant="body2" className={classes.cellMainText}>
                                                    {order.customer.displayName}
                                                </Typography>
                                                <Stack direction="row" gap="0.25rem" alignItems="center">
                                                    <FontAwesomeIcon
                                                        className={classes.cellMainText}
                                                        icon={faUserGroup}
                                                    />
                                                    <Box className={classes.cellMainText}>
                                                        {order.deliveryDetails.sittings}
                                                    </Box>
                                                </Stack>
                                            </Stack>
                                            {creatorPerson?.company?.name && (
                                                <Typography variant="caption" className={classes.tableHeadTextColor}>
                                                    {creatorPerson?.company?.name}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <FontAwesomeIcon icon={faChevronRight} className={classes.titleButton} />
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                </TableBody>
            </Table>
            {showPackSlipModal && selectedOrderId && (
                <OrderPackageSlip
                    orders={data?.orders ?? []}
                    onClose={handleClosePackSlip}
                    merchantId={merchantId}
                    id={selectedOrderId}
                    open={showPackSlipModal}
                />
            )}
        </>
    )
}

export function MeetingServiceInfo() {
    const { classes } = useStyles()

    return (
        <Grid container flexWrap="nowrap" alignItems="flex-start" className={classes.container}>
            {/* Delivery planning - hidden until we can make it work */}
            {false && (
                <LocalizedStrict
                    id="merchant-home-statistics-meeting-distribution-chart-tooltip"
                    attrs={{ title: true }}
                >
                    <Tooltip title="Shows the distribution of meeting service orders during today">
                        <Box>
                            <LocalizedStrict
                                id="merchant-home-meeting-service-delivery-planning-card"
                                attrs={{ title: true }}
                            >
                                <DashboardDataCard
                                    title="Delivery planning"
                                    slotProps={{
                                        card: { className: classes.card },
                                        graphWrapper: { className: classes.graphWrapper },
                                    }}
                                >
                                    <DashboardDataContents>
                                        <MeetingServiceInfoDeliveryPlanning />
                                    </DashboardDataContents>
                                </DashboardDataCard>
                            </LocalizedStrict>
                        </Box>
                    </Tooltip>
                </LocalizedStrict>
            )}
            <LocalizedStrict id="merchant-home-statistics-next-deliveries-card-tooltip" attrs={{ title: true }}>
                <Tooltip title="The next 5 deliveries for today">
                    <Box>
                        <LocalizedStrict
                            id="merchant-home-meeting-service-next-deliveries-card"
                            attrs={{ title: true }}
                            vars={{ numberOfOrders: NUMBER_OF_ORDERS }}
                        >
                            <DashboardDataCard
                                title={`Next ${NUMBER_OF_ORDERS} deliveries`}
                                slotProps={{
                                    card: { className: classes.card },
                                    graphWrapper: { className: classes.graphWrapper },
                                }}
                            >
                                <DashboardDataContents>
                                    <MeetingServiceInfoNextDeliveries />
                                </DashboardDataContents>
                            </DashboardDataCard>
                        </LocalizedStrict>
                    </Box>
                </Tooltip>
            </LocalizedStrict>
        </Grid>
    )
}
